// components/omore/OmoreTrends.tsx
// Week-over-week and month-over-month trend comparison dashboard

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    fetchWeekOverWeekComparison,
    fetchMonthOverMonthComparison,
} from '../../services/omoreInteractionService';
import type { WeekOverWeekComparison, TrendComparison } from '../../services/omoreInteractionService';
import './OmoreTrends.css';

const HELPER_TYPE_LABELS: Record<string, string> = {
    general: 'General Help',
    character: 'Character',
    setting: 'Setting',
    plot: 'Plot',
    dialogue: 'Dialogue',
    theme: 'Theme',
    worldBuilding: 'World Building',
    revision: 'Revision',
    brainstorm: 'Brainstorm',
};

export const OmoreTrends: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [comparison, setComparison] = useState<WeekOverWeekComparison | null>(null);
    const [period, setPeriod] = useState<'weekly' | 'monthly'>('weekly');


    const loadComparison = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = period === 'weekly'
                ? await fetchWeekOverWeekComparison()
                : await fetchMonthOverMonthComparison();
            setComparison(data);
        } catch (err: any) {
            console.error('Error loading trends:', err);
            setError(err.message || 'Failed to load trends');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        loadComparison();
    }, [loadComparison]);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const renderTrendCard = (trend: TrendComparison, inverse: boolean = false) => {
        const isPositive = inverse ? trend.trend === 'down' : trend.trend === 'up';
        const isNegative = inverse ? trend.trend === 'up' : trend.trend === 'down';
        const trendColor = isPositive ? '#10b981' : isNegative ? '#ef4444' : '#9ca3af';
        const trendIcon = trend.trend === 'up' ? '↑' : trend.trend === 'down' ? '↓' : '→';

        return (
            <div className="trend-card" key={trend.metric}>
                <div className="trend-metric-name">{trend.metric}</div>
                <div className="trend-values">
                    <span className="trend-current">{formatValue(trend.currentValue, trend.metric)}</span>
                    <span className="trend-arrow" style={{ color: trendColor }}>{trendIcon}</span>
                </div>
                <div className="trend-change" style={{ color: trendColor }}>
                    {trend.changePercent >= 0 ? '+' : ''}{trend.changePercent.toFixed(1)}%
                </div>
                <div className="trend-previous">
                    Previous: {formatValue(trend.previousValue, trend.metric)}
                </div>
            </div>
        );
    };

    const formatValue = (value: number, metric: string): string => {
        if (metric.includes('Rate') || metric.includes('Adoption')) {
            return `${value.toFixed(1)}%`;
        }
        if (metric.includes('Duration')) {
            return `${(value / 60).toFixed(1)}m`;
        }
        return value.toLocaleString();
    };

    return (
        <div className="omore-trends-container">
            <header className="omore-trends-header">
                <div className="header-content">
                    <div>
                        <h1>Ask Omore Trends</h1>
                        <p className="header-subtitle">
                            {period === 'weekly' ? 'Week-over-week' : 'Month-over-month'} comparison
                        </p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/omore')} className="back-button">← Omore Analytics</button>
                        <button onClick={loadComparison} className="refresh-button">↻ Refresh</button>
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">Sign Out</button>
                    </div>
                </div>
            </header>

            <div className="omore-trends-main">
                {/* Period toggle */}
                <section className="filters-section">
                    <div className="filter-row">
                        <div className="toggle-group">
                            <button
                                className={`toggle-btn ${period === 'weekly' ? 'active' : ''}`}
                                onClick={() => setPeriod('weekly')}
                            >
                                Weekly
                            </button>
                            <button
                                className={`toggle-btn ${period === 'monthly' ? 'active' : ''}`}
                                onClick={() => setPeriod('monthly')}
                            >
                                Monthly
                            </button>
                        </div>
                        {comparison && (
                            <div className="date-range-info">
                                <span>Current: {comparison.currentWeek.startDate} to {comparison.currentWeek.endDate}</span>
                                <span className="separator">vs</span>
                                <span>Previous: {comparison.previousWeek.startDate} to {comparison.previousWeek.endDate}</span>
                            </div>
                        )}
                    </div>
                </section>

                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading trends...</p>
                    </div>
                )}

                {error && (
                    <div className="error-state">
                        <p>{error}</p>
                        <button onClick={loadComparison} className="retry-button">Retry</button>
                    </div>
                )}

                {!loading && !error && comparison && (
                    <>
                        {/* Key Metrics Trends */}
                        <section className="trends-grid">
                            <h2>Key Metrics</h2>
                            <div className="trend-cards">
                                {renderTrendCard(comparison.trends.sessions)}
                                {renderTrendCard(comparison.trends.uniqueUsers)}
                                {renderTrendCard(comparison.trends.messages)}
                                {renderTrendCard(comparison.trends.avgSessionDuration)}
                                {renderTrendCard(comparison.trends.suggestionAdoptionRate)}
                                {renderTrendCard(comparison.trends.flagRate, true)}
                            </div>
                        </section>

                        {/* Growth & Decline Areas */}
                        <section className="areas-section">
                            <div className="two-col">
                                <div className="area-card growth">
                                    <h3>Top Growth Areas</h3>
                                    {comparison.topGrowthAreas.length === 0 && (
                                        <p className="empty-note">No significant growth areas</p>
                                    )}
                                    <ul className="area-list">
                                        {comparison.topGrowthAreas.map(area => (
                                            <li key={area} className="area-item growth-item">{area}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="area-card decline">
                                    <h3>Areas of Decline</h3>
                                    {comparison.topDeclineAreas.length === 0 && (
                                        <p className="empty-note">No significant decline areas</p>
                                    )}
                                    <ul className="area-list">
                                        {comparison.topDeclineAreas.map(area => (
                                            <li key={area} className="area-item decline-item">{area}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* Helper Type Trends */}
                        <section className="helper-trends-section">
                            <h2>Helper Type Trends</h2>
                            <div className="helper-trends-table-wrapper">
                                <table className="helper-trends-table">
                                    <thead>
                                        <tr>
                                            <th>Helper Type</th>
                                            <th>Current</th>
                                            <th>Previous</th>
                                            <th>Change</th>
                                            <th>% Change</th>
                                            <th>Trend</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {comparison.helperTypeTrends.map(ht => {
                                            const isPositive = ht.changePercent > 5;
                                            const isNegative = ht.changePercent < -5;
                                            const color = isPositive ? '#10b981' : isNegative ? '#ef4444' : '#9ca3af';
                                            return (
                                                <tr key={ht.helperType}>
                                                    <td>{HELPER_TYPE_LABELS[ht.helperType] || ht.helperType}</td>
                                                    <td>{ht.currentCount}</td>
                                                    <td>{ht.previousCount}</td>
                                                    <td style={{ color }}>{ht.change >= 0 ? '+' : ''}{ht.change}</td>
                                                    <td style={{ color }}>{ht.changePercent >= 0 ? '+' : ''}{ht.changePercent.toFixed(1)}%</td>
                                                    <td>
                                                        <span className="trend-indicator" style={{ color }}>
                                                            {isPositive ? '↑' : isNegative ? '↓' : '→'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};
